import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { AtSignIcon, InfoIcon, MailIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { PasswordInput } from "@/features/account/components/PasswordInput";
import { UpdateEmailAddressSchema, updateEmailAddressSchema } from "@/features/account/schemas";
import { updateEmailAddress } from "@/features/account/services/account";
import Error from "@/shared/components/Error";
import Success from "@/shared/components/Success";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { User } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";

interface UpdateEmailAddressFormProps {
  user: User;
}

export default function UpdateEmailAddressForm({ user }: UpdateEmailAddressFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<UpdateEmailAddressSchema>({
    resolver: zodResolver(updateEmailAddressSchema),
    defaultValues: {
      emailAddress: user.emailAddress,
      currentPassword: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, UpdateEmailAddressSchema>({
    mutationFn: updateEmailAddress,
    onSuccess: (emailAddress) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_DETAILS], exact: true });
      form.reset({ emailAddress, currentPassword: "" });
    },
  });

  async function onSubmit(values: UpdateEmailAddressSchema) {
    mutation.mutate(values);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MailIcon className="size-4 text-primary" />
          <CardTitle>Email address</CardTitle>
        </div>
        <CardDescription>Used to sign in, to receive notifications and to identify you on project invitations.</CardDescription>
      </CardHeader>
      <CardContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {mutation.isError && <Error title="Unable to update email address" error={mutation.error} />}
            {mutation.isSuccess && !form.formState.isDirty && <Success title="Email address updated" description="Use your new email address the next time you sign in." />}
            <FieldGroup>
              <div className="flex items-center justify-between gap-4 border border-border bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <AtSignIcon className="size-3.5 shrink-0" />
                  Current email address
                </div>
                <span className="text-xs font-medium">{user.emailAddress}</span>
              </div>
              <Controller
                name="emailAddress"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>New email address</FieldLabel>
                    <Input {...field} id={field.name} type="email" disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="john.smith@example.com" autoComplete="email" />
                    <FieldDescription>You will need to verify the new address before it becomes active.</FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Controller
                name="currentPassword"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Current password</FieldLabel>
                    <PasswordInput {...field} id={field.name} disabled={mutation.isPending} aria-invalid={fieldState.invalid} placeholder="••••••••" autoComplete="current-password" />
                    <FieldDescription>Confirm it&rsquo;s you before changing where your sign in link is sent.</FieldDescription>
                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                  </Field>
                )}
              />
              <Alert>
                <InfoIcon />
                <AlertTitle>Changing your email affects sign in</AlertTitle>
                <AlertDescription>Your existing sessions stay active, but you must use the new address to sign in from then on.</AlertDescription>
              </Alert>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" size="sm" disabled={mutation.isPending || !form.formState.isDirty} onClick={() => form.reset()}>
                  Cancel
                </Button>
                <Button size="sm" disabled={mutation.isPending || !form.formState.isDirty}>
                  <MailIcon />
                  {mutation.isPending ? "Updating..." : "Update email"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
